import { FONT_SIZE_KEYS } from "constants/theme";
import { Theme } from "styled";
import { round } from "utils/number";

const zIndex = {
  modal: 9999,
  nav: 8888,
};

// TYPOGRAPHY

const BASE_FONT_SIZE = 18;
const BASE_LINE_HEIGHT_ADDEND = 0.5;
const BASE_LETTER_SPACING = -0.01;
const FONT_SCALE = 4 / 3;
const BASE_FONT_SIZE_MOBILE = 16;
const FONT_SCALE_MOBILE = 5 / 4;

// generate font properties based on a perfect fourth (large screens) and perfect fifth (small screens) font scales

const fontSizes: { [key: (typeof FONT_SIZE_KEYS)[number]]: number } =
  FONT_SIZE_KEYS.reduce(
    (acc, key, index) => ({
      ...acc,
      [key]: round(BASE_FONT_SIZE * FONT_SCALE ** (index - 2)),
    }),
    {}
  ); // pixels

const fontSizesMobile: {
  [key: (typeof FONT_SIZE_KEYS)[number]]: number;
} = FONT_SIZE_KEYS.reduce(
  (acc, key, index) => ({
    ...acc,
    [key]: round(BASE_FONT_SIZE_MOBILE * FONT_SCALE_MOBILE ** (index - 2)),
  }),
  {}
); // pixels

const fontSizesUI = {
  xs: 10,
  sm: 12,
  md: 14,
  lg: 16,
  xl: 18,
};

const lineHeights: { [key: (typeof FONT_SIZE_KEYS)[number]]: number } =
  FONT_SIZE_KEYS.reduce(
    (acc, key, index) => ({
      ...acc,
      [key]: round(
        1 + BASE_LINE_HEIGHT_ADDEND * FONT_SCALE ** ((-index + 2) * 1.5),
        2
      ),
    }),
    {}
  ); // unitless

const letterSpacings: {
  [key: (typeof FONT_SIZE_KEYS)[number]]: number;
} = FONT_SIZE_KEYS.reduce(
  (acc, key, index) => ({
    ...acc,
    [key]: round(BASE_LETTER_SPACING * FONT_SCALE ** (index - 2), 2),
  }),
  {}
); // ems

// APPEARANCE

const DEFAULT_COLORS = {
  white: "#FFFFFF",
  lightGrey: "#FEFEFE",
  aqua: "#93F2FF",
  midnight: "#1D1933",
  violet: "#18009C",
  sapphire: "#3130B0",
  periwinkle: "#5170CA",
  mint: "#19D3C6",
  cerise: "#E5345E",
  cosmos: "#FFEE93",
  leaf: "#B3E673",
  dust: "#8A8A99",
  tree: "#218913",
};

const colors = {
  // These aren't strictly black and white, but should be close
  text: "#090909",
  textBody: "#333333",
  textReverse: DEFAULT_COLORS.white,
  textReverseBody: "#EEEEEE",
  background: DEFAULT_COLORS.lightGrey,
  uiBackground: DEFAULT_COLORS.white,

  // These aren't necessarily primary and secondary brand colors
  // They are used for UI elements like buttons
  primary: DEFAULT_COLORS.violet,
  secondary: DEFAULT_COLORS.aqua,

  // These can be splashier brand colors that may not be appropriate for general UI
  // If not defined, they will be generated based on the above colors
  tertiary: DEFAULT_COLORS.periwinkle,
  accent: DEFAULT_COLORS.mint,
  highlight: DEFAULT_COLORS.violet,
  shadow: DEFAULT_COLORS.midnight,

  // These will generally not be overridden by themes
  danger: DEFAULT_COLORS.cerise,
  success: DEFAULT_COLORS.leaf,
  warning: DEFAULT_COLORS.cosmos,
  disabled: DEFAULT_COLORS.dust,
  credit: DEFAULT_COLORS.tree,
};

const borderRadius = {
  micro: 4,
  small: 12,
  regular: 16,
  large: 24,
  button: 8,
};

// ANIMATION

const ease = {
  outQuart: "cubic-bezier(0.25, 1, 0.5, 1)",
};

const transitions = {
  default: `0.3s ${ease.outQuart}`,
  simple: `0.15s ease-out`,
};

const fonts = {
  body: '"Lato", "Helvetica", "Arial", sans-serif',
  heading: '"Lato", "Helvetica", "Arial", sans-serif',
};

const fontWeights = {
  normal: 400,
  bold: 700,
  black: 900,
};

export default {
  zIndex,
  fontSizes,
  fontSizesMobile,
  fontSizesUI,
  fonts,
  fontWeights,
  lineHeights,
  letterSpacings,
  colors,
  reverseColors: {}, // Will be generated based on colors
  shadows: {}, // Will be generated based on colors
  borderRadius,
  ease,
  transitions,
} as Theme;
